import { t, fill, ISSUE_LABELS, type Lang } from '../i18n.js';
import { RASTER_DPI_CHOICES, type RasterRunResult } from '../pipeline.js';
import { MEASURED_RASTER_ACCURACY as MEASURED } from '../raster-accuracy.js';
import { InkLedger } from './InkLedger.js';

/**
 * The scan surface, whose feature is its honesty.
 *
 * `docs/HANDOFF.md` on the raster work: *"Output there is uncertain by nature and
 * the interface has to convey confidence rather than fact. A number the interface
 * presents wrongly is worse than one it does not present, because the reader
 * cannot know."*
 *
 * So this panel is built the other way up from the conversion surface. That one
 * leads with the outcome, because there the outcome is exact. Here the outcome is
 * an estimate, so the first thing on the page is what the estimate is worth: the
 * measured figure, the bar it is held to, the fact that it misses it, and the
 * published range for comparable systems. Then the detections, each with its own
 * confidence. Then what could not be settled and is waiting for a person.
 *
 * Nothing here is decoration. Every number is either measured at run time from
 * this scan or read from `raster-accuracy.ts`, which a test holds to the committed
 * acceptance report.
 */
export function RasterStep({
  result,
  lang,
  dpi,
  onDpi,
}: {
  result: RasterRunResult;
  lang: Lang;
  dpi: number;
  onDpi: (dpi: number) => void;
}): JSX.Element {
  const s = (k: Parameters<typeof t>[0]): string => t(k, lang);
  const { merge } = result;
  // Only the merge's findings. The ledger's located regions are accounting and
  // live in their own fold below: pouring 248 of them in here made a review
  // queue that was 99% things nobody has to review.
  const issues = merge.issues;

  return (
    <section className="raster-result">
      {/*
        The caveat comes first and is not foldable. A scan surface that looks as
        confident as the vector surface is the most expensive defect available on
        this page, and a caveat below the results is a caveat nobody read.
      */}
      <section
        className="raster-caveat"
        data-gate="raster"
        data-passed={String(MEASURED.passed)}
      >
        <h3>{s('scanAccuracyTitle')}</h3>
        {/*
          The verdict is read off the measurement, never written into the markup.
          For every release until this one it said the gate failed, and hard-coding
          that sentence would have been correct every single time — right up to the
          release where it silently stopped being true. The same applies now in
          reverse, which is the more dangerous direction.
        */}
        <p className="raster-verdict">{s(MEASURED.passed ? 'scanGateMeets' : 'scanGateFails')}</p>
        <p>
          {fill(s('scanAccuracyBody'), {
            line: MEASURED.worstGatedLineF1.toFixed(3),
            precision: MEASURED.worstGatedLinePrecision.toFixed(3),
            recall: MEASURED.worstGatedLineRecall.toFixed(3),
            circle: MEASURED.worstGatedCircleF1.toFixed(3),
            polyline: MEASURED.worstGatedPolylineF1.toFixed(3),
            cleanBar: MEASURED.cleanBar.toFixed(2),
            noisyBar: MEASURED.noisyBar.toFixed(2),
            low: MEASURED.publishedRange[0].toFixed(2),
            high: MEASURED.publishedRange[1].toFixed(2),
          })}
        </p>
        <p className="note">
          {fill(s('scanAccuracyGap'), {
            drawings: String(MEASURED.corpusDrawings),
            worst: MEASURED.everyImageWorstF1.toFixed(3),
          })}
        </p>
        <p className="note">
          {s('scanReportNote')}: <code>{MEASURED.report}</code> · <code>{MEASURED.detector}</code>
        </p>
      </section>

      <section className="step raster-how">
        <h3>{s('scanResolution')}</h3>
        <div className="dpi-choices" role="group" aria-label={s('scanResolution')}>
          {RASTER_DPI_CHOICES.map((choice) => (
            <button
              key={choice}
              data-dpi-choice={choice}
              className={choice === dpi ? 'on' : ''}
              onClick={() => onDpi(choice)}
            >
              {choice} DPI
            </button>
          ))}
        </div>
        <p className="note">{s('scanResolutionNote')}</p>

        <dl className="raster-facts">
          <div data-raster-dpi={result.dpi}>
            <dt>{s('scanResolution')}</dt>
            <dd>
              {result.dpi} DPI · {result.widthPx} × {result.heightPx} px
            </dd>
          </div>
          <div data-raster-skew={result.skewDegrees}>
            <dt>{s('scanSkew')}</dt>
            <dd>{result.skewDegrees.toFixed(1)}°</dd>
          </div>
          {/*
            The ink share and the detection count used to sit here and now do
            not. Both are stated more precisely a few centimetres below — the
            stamp gives ink as an exact count rather than a share of every pixel
            on the sheet, and the detections fold carries its own count in its
            summary. Two figures for one quantity is how a reader ends up
            believing the less precise one: "ink 1.74%" beside "151,166 ink
            pixels" is one measurement said twice, and only one of them is a
            number anybody can check against the account.
          */}
        </dl>
        <p className="note">{s('scanSkewNote')}</p>
      </section>

      {/*
        In the main flow and never inside a fold, by the rule this file already
        follows: what has to be read to know what the scan is worth stays open.
        This is an accounting claim rather than a diagnostic — it is the only
        thing on the surface that says what the detector did NOT name — and a
        surface reporting fifteen detections with the unexplained ink folded away
        would be the overstatement this whole panel is built to avoid.
      */}
      <InkLedger ledger={result.ledger} lang={lang} />

      {/*
        Scale, refused, in the same visual language the conversion surface uses for
        the same refusal. Rasterising resolves nothing, so this can never become a
        download here.
      */}
      <section className="step step-todo">
        <h3>{s('scanNoDxf')}</h3>
        <p>{s('scanNoDxfWhy')}</p>
      </section>

      {result.vectorEntityCount > 0 && (
        <details className="fold raster-merge" data-fold="raster-merge">
          {/*
            Folded, with the headline on the summary. The merge counts are a
            diagnostic — nothing here has to be read to know what the scan is worth,
            which is the same test the conversion surface applies to its own folds —
            and unfolding all of it put this surface over two screens.
          */}
          <summary data-merge={merge.counts.vector}>
            {s('scanMerge')}
            <span className="fold-count">
              {merge.counts.vector} / {merge.counts.raster}
            </span>
          </summary>
          <ul className="counts">
            <li>
              <span>{s('scanMergeVector')}</span>
              <b>{merge.counts.vector}</b>
            </li>
            <li>
              <span>{s('scanMergeRaster')}</span>
              <b>{merge.counts.raster}</b>
            </li>
            <li>
              <span>{s('scanMergeSuppressed')}</span>
              <b>{merge.counts.suppressed}</b>
            </li>
            <li>
              <span>{s('scanMergeConflicts')}</span>
              <b>{merge.counts.conflicts}</b>
            </li>
          </ul>
          <p className="note">{s('scanMergeNote')}</p>
        </details>
      )}

      {/*
        The two per-entity lists are folded, and the caveat above them is not.
        Same rule the conversion surface follows: what has to be read to finish the
        job stays open, and a measurement nobody has to read is still present but
        does not push the page past two screens. The counts are in the summaries, so
        a fold never hides the fact that there is something inside it.
      */}
      <details className="fold" data-fold="raster-issues">
        <summary>
          {s('scanIssues')}
          <span className="fold-count">
            {issues.length === 0 ? s('scanNoIssues') : issues.length}
          </span>
        </summary>
        {issues.length === 0 ? (
          <p className="note" data-issue="none">
            {s('scanNoIssues')}
          </p>
        ) : (
          <ul className="raster-issue-list">
            {issues.map((issue, index) => (
              <li key={`${issue.kind}-${index}`} data-issue={issue.kind}>
                <b>{(ISSUE_LABELS[issue.kind] ?? { en: issue.kind, ja: issue.kind })[lang]}</b>
                <span>{issue.detail}</span>
              </li>
            ))}
          </ul>
        )}
      </details>

      {/*
        What the detector named, and what it could not — one fold, because they
        are one question. They were briefly two, which put a fourth top-level
        fold on a surface held to two screens and took it 9 px over; the
        alternative was shaving single-sentence copy, and the copy was doing
        more work than the separation was.

        Both lists are capped and scrolled inside themselves. The unexplained
        list is as long as the drawing is complex — 248 regions on the sample
        scan — and an uncapped list inside an opened fold grows the page without
        bound, which is U5b in the first-use register. The counts are in the
        summary, so the fold never hides that there is something inside it.
      */}
      <details className="fold" data-fold="raster-entities">
        <summary>
          {s('scanDetected')} · {s('inkUnexplained')}
          <span className="fold-count">
            {result.rasterEntities.length} /{' '}
            {result.ledger.located.length + (result.ledger.diffuse.regions > 0 ? 1 : 0)}
          </span>
        </summary>

        <h4 className="fold-heading">
          {s('scanDetected')} · {s('scanConfidence')}
        </h4>
        {result.rasterEntities.length === 0 ? (
          <p className="note">{s('scanNothingFound')}</p>
        ) : (
          <ul className="raster-entity-list">
            {result.rasterEntities.map((entity) => (
              <li key={entity.id} data-confidence={entity.confidence ?? 0}>
                <code>{entity.type}</code>
                <span className="raster-bar" aria-hidden="true">
                  {/*
                    The bar is a second encoding of the same number, never the
                    only one: ADR 0007 settled that a reading must not depend on
                    colour, and a length is no better on its own.
                  */}
                  <i style={{ width: `${Math.round((entity.confidence ?? 0) * 100)}%` }} />
                </span>
                <b>{(entity.confidence ?? 0).toFixed(3)}</b>
              </li>
            ))}
          </ul>
        )}

        <h4 className="fold-heading">{s('inkUnexplained')}</h4>
        {/*
          Rendered from the ledger rather than from `inkIssues`, and the reason
          is the language. A `QualityIssue.detail` is English prose minted in
          `packages/core`, which has no business knowing what language a reader
          speaks; with a couple of merge findings that was tolerable, and with
          248 located regions it would put 248 English sentences under Japanese
          labels on a surface whose drawings are Japanese.

          The ledger carries the same facts as numbers — pixels, a box, the
          detections a region runs alongside — so the sentence is built here in
          whichever language is on screen. `inkIssues` is unchanged and is still
          what the contract and the acceptance report consume.
        */}
        {result.ledger.located.length === 0 && result.ledger.diffuse.regions === 0 ? (
          <p className="note" data-ink-issue="none">
            {s('inkAllExplained')}
          </p>
        ) : (
          <>
            <p className="note">{s('inkUnexplainedNote')}</p>
            <ul className="raster-issue-list">
              {result.ledger.located.map((region, index) => {
                const box = region.bbox
                  .map((v) => (v / (result.dpi / 72)).toFixed(1))
                  .join(', ');
                const ids = region.abuts.map((d) => `r${String(d).padStart(4, '0')}`).join(', ');
                return (
                  <li key={`ink-${index}`} data-issue="unrecognised_ink">
                    <b>{ISSUE_LABELS.unrecognised_ink![lang]}</b>
                    <span>
                      {fill(s(ids === '' ? 'inkRegion' : 'inkRegionBeside'), {
                        pixels: region.pixels.toLocaleString('en-US'),
                        box,
                        ids,
                      })}
                    </span>
                  </li>
                );
              })}
              {result.ledger.diffuse.regions > 0 && (
                <li data-issue="unrecognised_ink" data-ink-diffuse={result.ledger.diffuse.regions}>
                  <b>{ISSUE_LABELS.unrecognised_ink![lang]}</b>
                  <span>
                    {fill(s('inkDiffuse'), {
                      regions: result.ledger.diffuse.regions.toLocaleString('en-US'),
                      pixels: result.ledger.diffuse.pixels.toLocaleString('en-US'),
                      floor: String(result.ledger.minRegionPoints),
                    })}
                  </span>
                </li>
              )}
            </ul>
          </>
        )}
      </details>

    </section>
  );
}
